import { useEffect, useState } from "react";
import { api, API_BASE, formatMoney, openDownload } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";
import { Mail, TrendingUp, TrendingDown, Wallet, CalendarClock, FileDown, FileText, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend, ComposedChart, Line } from "recharts";

const PIE = ["#1E2A9C", "#29B7E3", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6"];

export default function Reports() {
  const { currency } = useCurrency();
  const [sum, setSum] = useState(null);
  const [sending, setSending] = useState(false);
  const now = new Date();
  const [kdvYear, setKdvYear] = useState(now.getFullYear());
  const [kdvMonth, setKdvMonth] = useState(now.getMonth() + 1);
  const [kdv, setKdv] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [pnlYear, setPnlYear] = useState(now.getFullYear());
  const [cf, setCf] = useState(null);

  useEffect(() => {
    (async () => setSum((await api.get(`/reports/summary?currency=${currency}`)).data))();
  }, [currency]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/reports/kdv?year=${kdvYear}&month=${kdvMonth}`);
        setKdv(data);
      } catch (err) { console.error("KDV yüklenemedi", err); }
    })();
  }, [kdvYear, kdvMonth]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/reports/pnl?currency=${currency}&year=${pnlYear}`);
        setPnl(data);
      } catch (err) { console.error("P&L yüklenemedi", err); }
    })();
  }, [pnlYear, currency]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/reports/cashflow-projection?weeks=12`);
        setCf(data);
      } catch (err) { console.error("Nakit projeksiyonu yüklenemedi", err); }
    })();
  }, []);

  const kdvPdf = () => {
    openDownload(`/api/reports/kdv.pdf`, { year: kdvYear, month: kdvMonth });
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reports/email", { currency });
      toast.success(`Rapor gönderildi: ${data.to}`);
    } catch {
      toast.error("E-posta gönderilemedi");
    }
    setSending(false);
  };

  const exportXlsx = () => {
    openDownload(`/api/export/report.xlsx`, { currency });
  };

  if (!sum) return <div className="p-8">Yükleniyor...</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Analiz</div>
          <h1 className="font-display text-4xl font-extrabold text-slate-900 mt-1">Finansal Raporlar</h1>
          <p className="text-slate-500 mt-1">{currency} para birimi üzerinden.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="export-report-xlsx" onClick={exportXlsx} className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white border border-slate-200 hover:border-emerald-500 text-slate-700 hover:text-emerald-600 font-bold transition">
            <FileDown className="w-4 h-4" /> Excel İndir
          </button>
          <button data-testid="send-report-email" onClick={sendEmail} disabled={sending} className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[#29B7E3] text-white font-bold hover:bg-[#1E90BF] shadow-lg shadow-cyan-500/20 disabled:opacity-60">
            <Mail className="w-4 h-4" /> {sending ? "Gönderiliyor..." : "E-posta ile Gönder"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="p-6 bg-white rounded-2xl border">
          <div className="flex justify-between"><span className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Gelir</span><TrendingUp className="w-4 h-4 text-emerald-500" /></div>
          <div className="font-mono-num text-3xl font-extrabold text-emerald-600 mt-3">{formatMoney(sum.income_total, currency)}</div>
        </div>
        <div className="p-6 bg-white rounded-2xl border">
          <div className="flex justify-between"><span className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Gider</span><TrendingDown className="w-4 h-4 text-rose-500" /></div>
          <div className="font-mono-num text-3xl font-extrabold text-rose-600 mt-3">{formatMoney(sum.expense_total, currency)}</div>
        </div>
        <div className="p-6 bg-white rounded-2xl border">
          <div className="flex justify-between"><span className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Kar/Zarar</span><Wallet className="w-4 h-4 text-blue-500" /></div>
          <div className="font-mono-num text-3xl font-extrabold text-[#1E2A9C] mt-3">{formatMoney(sum.profit, currency)}</div>
        </div>
        <div className="p-6 bg-white rounded-2xl border">
          <div className="flex justify-between"><span className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Vade</span><CalendarClock className="w-4 h-4 text-amber-500" /></div>
          <div className="font-mono-num text-xs text-slate-500 mt-3">Tahsilat: <span className="text-emerald-600 font-bold">{formatMoney(sum.upcoming_collections, currency)}</span></div>
          <div className="font-mono-num text-xs text-slate-500">Ödeme: <span className="text-rose-600 font-bold">{formatMoney(sum.upcoming_payments, currency)}</span></div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-2xl border">
        <div className="font-display text-xl font-bold mb-4">Aylık Gelir vs Gider</div>
        <div className="h-72">
          <ResponsiveContainer>
            <BarChart data={sum.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="month" stroke="#94A3B8" fontSize={11} />
              <YAxis stroke="#94A3B8" fontSize={11} />
              <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
              <Legend />
              <Bar dataKey="income" fill="#10B981" name="Gelir" radius={[6,6,0,0]} />
              <Bar dataKey="expense" fill="#EF4444" name="Gider" radius={[6,6,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-2xl border">
          <div className="font-display text-xl font-bold mb-4">Gelir Dağılımı (Kategori)</div>
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={sum.categories_income} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {sum.categories_income.map((entry, i) => <Cell key={entry.name || `inc-${i}`} fill={PIE[i % PIE.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white p-6 rounded-2xl border">
          <div className="font-display text-xl font-bold mb-4">Gider Dağılımı (Kategori)</div>
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={sum.categories_expense} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {sum.categories_expense.map((entry, i) => <Cell key={entry.name || `exp-${i}`} fill={PIE[i % PIE.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* 12-Week Cash Flow Projection */}
      {cf && (
        <div className="bg-white p-6 rounded-2xl border">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Nakit Akış Projeksiyonu</div>
              <div className="font-display text-2xl font-extrabold text-slate-900">12 Haftalık Nakit Tahmini (TRY)</div>
              <p className="text-xs text-slate-500 mt-1">Gelen çekler + tahsilatlar − verilen çekler − yaklaşan ödemeler − kredi taksitleri. TCMB kuruyla TRY'ye çevrilmiş.</p>
            </div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="text-center px-3 py-2 rounded-lg bg-emerald-50">
                <div className="text-[10px] uppercase text-emerald-600 font-bold">Toplam Giren</div>
                <div className="font-mono-num text-lg font-bold text-emerald-700">{formatMoney(cf.totals.inflow, "TRY")}</div>
              </div>
              <div className="text-center px-3 py-2 rounded-lg bg-rose-50">
                <div className="text-[10px] uppercase text-rose-600 font-bold">Toplam Çıkan</div>
                <div className="font-mono-num text-lg font-bold text-rose-700">{formatMoney(cf.totals.outflow, "TRY")}</div>
              </div>
              <div className={`text-center px-3 py-2 rounded-lg ${cf.totals.net >= 0 ? "bg-[#1E2A9C]/10" : "bg-amber-50"}`}>
                <div className={`text-[10px] uppercase font-bold ${cf.totals.net >= 0 ? "text-[#1E2A9C]" : "text-amber-600"}`}>Net</div>
                <div className={`font-mono-num text-lg font-bold ${cf.totals.net >= 0 ? "text-[#1E2A9C]" : "text-amber-700"}`}>{formatMoney(cf.totals.net, "TRY")}</div>
              </div>
            </div>
          </div>

          <div className="h-64">
            <ResponsiveContainer>
              <ComposedChart data={cf.weeks}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="week" stroke="#94A3B8" fontSize={11} />
                <YAxis stroke="#94A3B8" fontSize={11} />
                <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                <Legend />
                <Bar dataKey="inflow" fill="#10B981" name="Giren" radius={[4,4,0,0]} />
                <Bar dataKey="outflow" fill="#EF4444" name="Çıkan" radius={[4,4,0,0]} />
                <Line type="monotone" dataKey="cumulative" stroke="#1E2A9C" strokeWidth={2} name="Kümülatif Net" dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
                <tr>
                  <th className="text-left px-2 py-2">Hafta</th>
                  <th className="text-left px-2 py-2">Başlangıç</th>
                  <th className="text-right px-2 py-2">Giren</th>
                  <th className="text-right px-2 py-2">Çıkan</th>
                  <th className="text-right px-2 py-2">Net</th>
                  <th className="text-right px-2 py-2">Kümülatif</th>
                </tr>
              </thead>
              <tbody>
                {cf.weeks.map(w => (
                  <tr key={w.week} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 font-mono-num font-bold">{w.week}</td>
                    <td className="px-2 py-1.5 font-mono-num text-slate-600">{w.start}</td>
                    <td className="px-2 py-1.5 text-right font-mono-num text-emerald-600">{formatMoney(w.inflow, "TRY")}</td>
                    <td className="px-2 py-1.5 text-right font-mono-num text-rose-600">{formatMoney(w.outflow, "TRY")}</td>
                    <td className={`px-2 py-1.5 text-right font-mono-num font-bold ${w.net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{formatMoney(w.net, "TRY")}</td>
                    <td className={`px-2 py-1.5 text-right font-mono-num font-bold ${w.cumulative >= 0 ? "text-[#1E2A9C]" : "text-rose-700"}`}>{formatMoney(w.cumulative, "TRY")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Kar-Zarar (P&L) */}
      {pnl && (
        <div className="bg-white p-6 rounded-2xl border">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Yıllık Performans</div>
              <div className="font-display text-2xl font-extrabold text-slate-900">Kar-Zarar Tablosu (P&L)</div>
              <p className="text-xs text-slate-500 mt-1">Aylık gelir, gider (COGS + OpEx), brüt kar ve net kar ayrıntılı özet. {currency} bazında.</p>
            </div>
            <select data-testid="pnl-year" value={pnlYear} onChange={(e) => setPnlYear(Number(e.target.value))} className="px-3 py-2 border rounded-lg text-sm">
              {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
              <div className="text-[10px] uppercase font-bold text-emerald-600 tracking-widest">Toplam Gelir</div>
              <div className="font-mono-num font-bold text-emerald-700 mt-1">{formatMoney(pnl.totals.income, currency)}</div>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
              <div className="text-[10px] uppercase font-bold text-amber-600 tracking-widest">Doğrudan Maliyet (COGS)</div>
              <div className="font-mono-num font-bold text-amber-700 mt-1">{formatMoney(pnl.totals.cogs, currency)}</div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
              <div className="text-[10px] uppercase font-bold text-blue-600 tracking-widest">Brüt Kar</div>
              <div className="font-mono-num font-bold text-blue-700 mt-1">{formatMoney(pnl.totals.gross_profit, currency)}</div>
              <div className="text-[10px] text-slate-500">%{pnl.totals.gross_margin.toFixed(1)} marj</div>
            </div>
            <div className="p-3 bg-rose-50 rounded-lg border border-rose-100">
              <div className="text-[10px] uppercase font-bold text-rose-600 tracking-widest">Faaliyet Gideri (OpEx)</div>
              <div className="font-mono-num font-bold text-rose-700 mt-1">{formatMoney(pnl.totals.opex, currency)}</div>
            </div>
            <div className={`p-3 rounded-lg border ${pnl.totals.net_profit >= 0 ? "bg-[#1E2A9C]/5 border-[#1E2A9C]/20" : "bg-rose-50 border-rose-100"}`}>
              <div className={`text-[10px] uppercase font-bold tracking-widest ${pnl.totals.net_profit >= 0 ? "text-[#1E2A9C]" : "text-rose-600"}`}>Net Kar/Zarar</div>
              <div className={`font-mono-num font-extrabold text-lg mt-1 ${pnl.totals.net_profit >= 0 ? "text-[#1E2A9C]" : "text-rose-700"}`}>{formatMoney(pnl.totals.net_profit, currency)}</div>
              <div className="text-[10px] text-slate-500">%{pnl.totals.net_margin.toFixed(1)} marj</div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
                <tr>
                  <th className="text-left px-3 py-2">Ay</th>
                  <th className="text-right px-3 py-2">Gelir</th>
                  <th className="text-right px-3 py-2">COGS</th>
                  <th className="text-right px-3 py-2">Brüt Kar</th>
                  <th className="text-right px-3 py-2">OpEx</th>
                  <th className="text-right px-3 py-2">Net Kar</th>
                </tr>
              </thead>
              <tbody>
                {pnl.months.map(m => (
                  <tr key={m.month} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono-num font-bold text-slate-800">{m.month}</td>
                    <td className="px-3 py-2 text-right font-mono-num text-emerald-600">{formatMoney(m.income, currency)}</td>
                    <td className="px-3 py-2 text-right font-mono-num text-amber-600">{formatMoney(m.cogs, currency)}</td>
                    <td className="px-3 py-2 text-right font-mono-num text-blue-600 font-bold">{formatMoney(m.gross_profit, currency)}</td>
                    <td className="px-3 py-2 text-right font-mono-num text-rose-600">{formatMoney(m.opex, currency)}</td>
                    <td className={`px-3 py-2 text-right font-mono-num font-bold ${m.net_profit >= 0 ? "text-[#1E2A9C]" : "text-rose-600"}`}>{formatMoney(m.net_profit, currency)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 dark:bg-slate-800 font-bold">
                  <td className="px-3 py-2">TOPLAM</td>
                  <td className="px-3 py-2 text-right font-mono-num text-emerald-700">{formatMoney(pnl.totals.income, currency)}</td>
                  <td className="px-3 py-2 text-right font-mono-num text-amber-700">{formatMoney(pnl.totals.cogs, currency)}</td>
                  <td className="px-3 py-2 text-right font-mono-num text-blue-700">{formatMoney(pnl.totals.gross_profit, currency)}</td>
                  <td className="px-3 py-2 text-right font-mono-num text-rose-700">{formatMoney(pnl.totals.opex, currency)}</td>
                  <td className={`px-3 py-2 text-right font-mono-num ${pnl.totals.net_profit >= 0 ? "text-[#1E2A9C]" : "text-rose-700"}`}>{formatMoney(pnl.totals.net_profit, currency)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="mt-3 text-xs text-slate-500">💡 COGS (Doğrudan Maliyet): "alım", "alış", "maliyet", "malzeme", "hammadde", "stok" kategorilerini içeren gider işlemleri. Diğer giderler OpEx (Faaliyet Gideri) olarak sınıflandırılır.</div>
        </div>
      )}

      {/* KDV Beyanname */}
      <div className="bg-white p-6 rounded-2xl border">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Vergi Raporu</div>
            <div className="font-display text-2xl font-extrabold text-slate-900">KDV Beyanname Özeti</div>
            <p className="text-xs text-slate-500 mt-1">GİB KDV1 beyannamesi için hazırlanmış aylık özet. Satışlar proformalardan, alışlar gider işlemlerinden hesaplanır.</p>
            {kdv?.fx && (
              <p data-testid="kdv-fx-note" className="text-xs text-slate-500 mt-1 font-mono-num">
                Döviz çevrimi: USD/TRY {Number(kdv.fx.USD || 0).toFixed(4)} · EUR/TRY {Number(kdv.fx.EUR || 0).toFixed(4)} · Kur tarihi: {kdv.fx.date || "-"} · Kaynak: {kdv.fx.source || "-"}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <select data-testid="kdv-year" value={kdvYear} onChange={(e) => setKdvYear(Number(e.target.value))} className="px-3 py-2 border rounded-lg text-sm">
              {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select data-testid="kdv-month" value={kdvMonth} onChange={(e) => setKdvMonth(Number(e.target.value))} className="px-3 py-2 border rounded-lg text-sm">
              {["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"].map((m, i) => <option key={m} value={i+1}>{m}</option>)}
            </select>
            <button data-testid="kdv-pdf" onClick={kdvPdf} className="flex items-center gap-1 px-4 py-2 rounded-lg bg-[#1E2A9C] text-white text-sm font-bold hover:bg-[#121A68]">
              <Download className="w-4 h-4" /> PDF İndir
            </button>
          </div>
        </div>

        {kdv && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
              <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold">Hesaplanan KDV (Satış)</div>
              <div className="font-mono-num text-2xl font-extrabold text-emerald-700 mt-2">{formatMoney(kdv.sales.total_kdv, "TRY")}</div>
              <div className="text-xs text-slate-500 mt-1">Matrah: {formatMoney(kdv.sales.total_net, "TRY")}</div>
            </div>
            <div className="p-4 bg-rose-50 rounded-xl border border-rose-100">
              <div className="text-[10px] uppercase tracking-widest text-rose-600 font-bold">İndirilecek KDV (Alış)</div>
              <div className="font-mono-num text-2xl font-extrabold text-rose-700 mt-2">{formatMoney(kdv.purchases.total_kdv, "TRY")}</div>
              <div className="text-xs text-slate-500 mt-1">Brüt: {formatMoney(kdv.purchases.total_gross, "TRY")}</div>
            </div>
            <div className={`p-4 rounded-xl border ${kdv.payable_kdv >= 0 ? "bg-blue-50 border-blue-100" : "bg-amber-50 border-amber-100"}`}>
              <div className={`text-[10px] uppercase tracking-widest font-bold ${kdv.payable_kdv >= 0 ? "text-blue-600" : "text-amber-600"}`}>
                {kdv.payable_kdv >= 0 ? "Ödenecek KDV" : "Devreden KDV (İade)"}
              </div>
              <div className={`font-mono-num text-2xl font-extrabold mt-2 ${kdv.payable_kdv >= 0 ? "text-blue-700" : "text-amber-700"}`}>
                {formatMoney(Math.abs(kdv.payable_kdv), "TRY")}
              </div>
              <div className="text-xs text-slate-500 mt-1">Hesaplanan − İndirilecek</div>
            </div>
          </div>
        )}

        {kdv && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <div className="text-xs uppercase font-bold text-slate-500 tracking-widest mb-2">Satış — Oran Bazlı</div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr><th className="text-left px-3 py-2">Oran</th><th className="text-right px-3 py-2">Matrah</th><th className="text-right px-3 py-2">KDV</th><th className="text-right px-3 py-2">Toplam</th></tr>
                </thead>
                <tbody>
                  {kdv.sales.by_rate.length === 0 && <tr><td colSpan={4} className="py-4 text-center text-slate-400">Kayıt yok</td></tr>}
                  {kdv.sales.by_rate.map(r => (
                    <tr key={r.rate} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-mono-num font-bold">%{r.rate}</td>
                      <td className="px-3 py-2 text-right font-mono-num">{formatMoney(r.net, "TRY")}</td>
                      <td className="px-3 py-2 text-right font-mono-num text-emerald-600">{formatMoney(r.kdv, "TRY")}</td>
                      <td className="px-3 py-2 text-right font-mono-num font-bold">{formatMoney(r.gross, "TRY")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <div className="text-xs uppercase font-bold text-slate-500 tracking-widest mb-2">Alış / Gider — Oran Bazlı</div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr><th className="text-left px-3 py-2">Oran</th><th className="text-right px-3 py-2">Matrah</th><th className="text-right px-3 py-2">KDV</th><th className="text-right px-3 py-2">Brüt</th></tr>
                </thead>
                <tbody>
                  {kdv.purchases.by_rate.length === 0 && <tr><td colSpan={4} className="py-4 text-center text-slate-400">Kayıt yok</td></tr>}
                  {kdv.purchases.by_rate.map(r => (
                    <tr key={r.rate} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-mono-num font-bold">%{r.rate}</td>
                      <td className="px-3 py-2 text-right font-mono-num">{formatMoney(r.net, "TRY")}</td>
                      <td className="px-3 py-2 text-right font-mono-num text-rose-600">{formatMoney(r.kdv, "TRY")}</td>
                      <td className="px-3 py-2 text-right font-mono-num font-bold">{formatMoney(r.gross, "TRY")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
